//packages blocks
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import React, { useEffect, useReducer, useState } from "react";
//common blocks
import "./login.css";
import Checkbox from "../../components/checkbox";
import { FormInput, FormButton } from "../../components/form";
//styles, utils, assets and api blocks
import { signIn } from "../../api/auth";
import { BLACK, BLUE_EIGHT, GREY_SEVENTEEN, WHITE } from "../../theme";
import { setUser } from "../../redux/reducers/user";
import { setAlert } from "../../redux/reducers/alert";
import { validateSignIn } from "../../util/validation";
import logo from "../../assets/images/logo-v1.png";
import { set } from "lodash";

const Login = () => {
  const { t, i18n } = useTranslation();

  const [{ email, password, emailError, passwordError }, setData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      email: "",
      password: "",
      emailError: false,
      passwordError: false,
    }
  );
  const [isEnglish, setIsEnglish] = useState(true);
  const [isArabic, setIsArabic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disableButton, setDisabledButton] = useState(false);
  const [checkedOne, setCheckedOne] = useState(false);
  const [disableLoginForMins, setDisableLoginForMins] = useState(null);
  const [timer, setTimer] = useState(0);
  const [activateTimer, setActivateTimer] = useState(null);
  const [loginButtonTitle, setLoginButtonTitle] = useState(
    `${t("text_sign_in")}`
  );

  const dispatch = useDispatch();

  useEffect(() => {
    const calculateTimer = () => {
      setTimer((prev) => {
        // clear timer if it reaches 1 sec
        if (prev === 1000) {
          setDisableLoginForMins(null);
          setActivateTimer(false);
          setDisabledButton(false);
        }
        return prev - 1000;
      });
    };

    let timerInterval;
    if (activateTimer) {
      setTimer(disableLoginForMins * 60 * 1000);
      timerInterval = setInterval(calculateTimer, 1000);
    } else {
      clearInterval(timerInterval);
    }

    return () => {
      clearInterval(timerInterval);
    };
  }, [activateTimer, disableLoginForMins]);

  useEffect(() => {
    if (timer > 0) setLoginButtonTitle(`Retry in ${timer / 1000} seconds`);
    else setLoginButtonTitle(`${t("text_sign_in")}`);
  }, [t, timer]);

  useEffect(() => {
    document.title = "Login - Paynest";
  }, []);

  const showErrorAlert = (message) => {
    dispatch(
      setAlert({
        severity: "error",
        message,
        show: true,
      })
    );
  };

  const handleSignIn = async () => {
    if (!checkedOne) {
      showErrorAlert("Please accept terms and conditions");
      return;
    }
    const { status, message, type } =
      checkedOne && validateSignIn(email, password);
    if (!status) {
      setData({
        [type]: true,
      });
      showErrorAlert(message);
      return;
    }
    try {
      setLoading(true);

      const payload = {
        email: email.toLowerCase().trim(),
        password,
      };
      const response = await signIn(payload);
      if (response.status) {
        dispatch(setUser({ ...response, profileImg: "" }));
      } else {
        showErrorAlert(response?.message);
        if (response?.retryInMins && response?.retryInMins > 0) {
          setDisableLoginForMins(parseInt(response?.retryInMins ?? 0));
          setActivateTimer(true);
          setDisabledButton(true);
        }
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      showErrorAlert(
        err.message || err || t("text_something_went_wrong_try_again_later")
      );
    }
  };

  const handleChange = (prop) => (event) => {
    setData({
      [prop]: event.target.value,
      emailError: false,
      passwordError: false,
    });
  };
  const history = useHistory();

  const forgetClickHandler = () => {
    history.push("/forgotPassword");
  };

  const updateOne = () => setCheckedOne(!checkedOne);

  const changeLanguage = (lng) => {
    if (lng === "en") {
      setIsEnglish(true);
      setIsArabic(false);
    } else {
      setIsArabic(true);
      setIsEnglish(false);
    }

    i18n.changeLanguage(lng);
  };

  return (
    <div className="login-page">
      <div className="loginForm">
        <div className="payNest_logo_login">
          <img src={logo} />
        </div>

        <div className="login-form-group">
          <Box>
            <Typography
              variant="h3"
              style={{
                marginBottom: 10,
                marginLeft: 8,
                color: BLACK,
                fontWeight: 900,
                fontSize: "1.18vw",
              }}
            >
              {t("text_sign_in")}
            </Typography>
            <Typography
              variant="body2"
              style={{ marginLeft: 8, fontSize: "0.8vw" }}
            >
              {t("text_please_enter_your_registered_email_id_and_password")}
            </Typography>
          </Box>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="login-form-input">
              <FormInput
                error={emailError}
                value={email}
                onChange={handleChange}
                label={t("text_email_address")}
                name="email"
              />
            </div>

            <div className="login-form-input">
              <FormInput
                error={passwordError}
                value={password}
                onChange={handleChange}
                label={t("text_password")}
                name="password"
                type="password"
              />
            </div>

            <Box
              style={{
                marginTop: 15,
                fontWeight: "500",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <a
                href={true}
                onClick={forgetClickHandler}
                style={{
                  color: BLUE_EIGHT,
                  fontSize: "0.78vw",
                  fontFamily: "Montserrat, sans-serif",
                  lineHeight: "18px",
                  fontWeight: "bold",
                }}
              >
                {t("text_forgot_password")}
              </a>
            </Box>

            <Box
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Checkbox
                style={{
                  textDecoration: "underline",
                  fontSize: "0.73vw",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                }}
                name="a"
                label={t("text_i_accept_terms_and_conditions")}
                checked={checkedOne}
                onChange={updateOne}
              ></Checkbox>
            </Box>

            <div style={{ marginTop: 20 }}>
              <FormButton
                disabled={disableButton}
                type="submit"
                height={64}
                title={loginButtonTitle}
                style={{
                  fontSize: "0.9vw",
                  fontWeight: "700",
                  textTransform: "none",
                }}
                overRideButtonCheck={true}
                backgroundColor={BLUE_EIGHT}
                titleColor={WHITE}
                hoverTitleColor={WHITE}
                hoverBackgroundColor={BLUE_EIGHT}
                hoverBorderColor={WHITE}
                loading={loading}
                borderTopLeftRadius={15}
                borderTopRightRadius={15}
                borderBottomRightRadius={15}
                borderBottomLeftRadius={15}
                onClickHandler={handleSignIn}
              // skipRoleCheck={true}
              />
            </div>

            <Box display="flex" justifyContent="center" mt={4} mb={2}>
              <Typography
                variant="h3"
                style={{
                  color: isEnglish ? BLACK : GREY_SEVENTEEN,
                  cursor: "pointer",
                  borderRight: `2px solid ${BLACK}`,
                  paddingRight: 5,
                }}
                onClick={() => changeLanguage("en")}
              >
                {t("text_english")}
              </Typography>

              <Typography
                variant="h3"
                style={{
                  color: isArabic ? BLACK : GREY_SEVENTEEN,
                  cursor: "pointer",
                  marginLeft: 5,
                }}
                onClick={() => changeLanguage("ar")}
              >
                {t("text_arabic")}
              </Typography>
            </Box>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
